import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { TextField, Button, Chip, Box, Grid, InputAdornment, Typography } from '@mui/material';
import { IoSearch } from 'react-icons/io5';
import { LanguageContext } from '../../../../../../../../../../../libs/Context';

interface TagsSliderProps {
	tags?: string[]; // Tags passed from parent
	initialSelectedTags?: string[]; // Initially selected tags
	onChange?: (selectedTags: string[]) => void; // Callback when selection changes
}

// Styles for reuse
const CHIP_STYLES = {
	fontSize           : '12px',
	height             : '28px',
	padding            : '4px 8px',
	m                  : 0.5,
	'& .MuiChip-label' : {
		fontSize   : '12px !important',
		fontWeight : '400'
	},
	'& .MuiChip-deleteIcon' : {
		fontSize  : '16px',
		color     : '#666',
		margin    : '0 -6px 0 4px',
		'&:hover' : {
			color : '#333'
		}
	}
};

const SEARCH_FIELD_STYLES = {
	backgroundColor            : '#F4F4F4',
	borderRadius               : '4px',
	mb                         : 2,
	'& .MuiOutlinedInput-root' : {
		border       : 'none',
		'& fieldset' : {
			border : 'none'
		},
		'&:hover fieldset' : {
			border : 'none'
		},
		'&.Mui-focused fieldset' : {
			border : 'none'
		}
	},
	'& .MuiInputBase-root' : {
		boxShadow : 'none'
	}
};

const ADD_BUTTON_STYLES = {
	color           : 'var(--whiteColor)',
	backgroundColor : '#4CB3D0',
	'&:hover'       : {
		backgroundColor : '#3CA3C0'
	},
	'&.Mui-disabled' : {
		backgroundColor : '#E0E0E0',
		color           : '#A0A0A0'
	}
};

const TagSlider: React.FC<TagsSliderProps> = ({
	tags: propTags = [],
	initialSelectedTags = [],
	onChange
}) =>
{
	const { language } = useContext(LanguageContext);

	// Remove default tags - only use prop tags
	const defaultTags = useMemo(() => [], []);

	// State management with proper initializers to avoid recalculations
	const [ allTags, setAllTags ] = useState<string[]>(() =>
		[ ...new Set([ ...propTags, ...defaultTags ]) ]
	);
	const [ selectedTags, setSelectedTags ] = useState<string[]>(initialSelectedTags);
	const [ inputValue, setInputValue ] = useState('');
	const [ searchQuery, setSearchQuery ] = useState('');

	// Update selectedTags if initialSelectedTags changes
	useEffect(() =>
	{
		if (JSON.stringify(initialSelectedTags) !== JSON.stringify(selectedTags))
		{
			setSelectedTags(initialSelectedTags);
		}
	}, [ initialSelectedTags ]);

	// Update parent component when selected tags change (debounced)
	useEffect(() =>
	{
		if (onChange)
		{
			onChange(selectedTags);
		}
	}, [ selectedTags, onChange ]);

	// Handle adding a new tag - memoized to prevent recreation on every render
	const handleAddTag = useCallback(() =>
	{
		const trimmedTag = inputValue.trim();

		if (!trimmedTag) return;

		setInputValue(''); // Clear input field immediately for better UX

		if (!allTags.includes(trimmedTag))
		{
			// Add to all tags and selected tags
			setAllTags((prevTags) => [ ...prevTags, trimmedTag ]);
			setSelectedTags((prevSelected) => [ ...prevSelected, trimmedTag ]);
		}
		else if (!selectedTags.includes(trimmedTag))
		{
			// Tag exists but not selected, so select it
			setSelectedTags((prevSelected) => [ ...prevSelected, trimmedTag ]);
		}
	}, [ inputValue, allTags, selectedTags ]);

	// Handle toggling a tag's selection - memoized
	const toggleSelection = useCallback((tag: string) =>
	{
		setSelectedTags((prevSelected) =>
			(prevSelected.includes(tag)
				? prevSelected.filter((t) => t !== tag)
				: [ ...prevSelected, tag ])
		);
	}, []);

	// Handle removing a tag from selection - memoized
	const handleRemoveTag = useCallback((tag: string) =>
	{
		setSelectedTags((prevSelected) => prevSelected.filter((t) => t !== tag));
	}, []);

	// Filter tags based on search query - memoized to avoid recalculation
	const filteredTags = useMemo(() =>
	{
		if (!searchQuery) return allTags;
		const query = searchQuery.toLowerCase();

		return allTags.filter((tag) => tag.toLowerCase().includes(query));
	}, [ allTags, searchQuery ]);

	// Handle Enter key in the input field - memoized
	const handleKeyDown = useCallback((e: React.KeyboardEvent) =>
	{
		if (e.key === 'Enter' && inputValue.trim())
		{
			e.preventDefault();
			handleAddTag();
		}
	}, [ inputValue, handleAddTag ]);

	// Render tag chips - memoized to prevent unnecessary recreations
	const renderTagChips = useMemo(() => (
		filteredTags.map((tag, index) =>
		{
			const isSelected = selectedTags.includes(tag);

			return (
				<Chip
					key={`${tag}-${index}`}
					label={tag}
					onClick={() => toggleSelection(tag)}
					onDelete={isSelected ? () => handleRemoveTag(tag) : undefined}
					sx={{
						...CHIP_STYLES,
						backgroundColor : isSelected ? '#C4ECF8' : 'transparent',
						borderColor     : isSelected ? '#C4ECF8' : '#E0E0E0',
						'&:hover'       : {
							backgroundColor : isSelected ? '#B4E6F6' : '#F0F0F0'
						}
					}}
				/>
			);
		})
	), [ filteredTags, selectedTags, toggleSelection, handleRemoveTag ]);

	return (
		<Grid container>

			{/* Search tags */}
			<TextField
				fullWidth
				size='small'
				placeholder={language?.searchTags || 'Search Tags'}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				disabled={allTags.length === 0}
				sx={SEARCH_FIELD_STYLES}
				InputProps={{
					startAdornment : (
						<InputAdornment position='start'>
							<IoSearch />
						</InputAdornment>
					)
				}}
			/>

			{/* Display Tags as Chips */}
			{(filteredTags.length > 0 || searchQuery) && (
				<Box sx={{
					display        : 'flex',
					flexWrap       : 'wrap',
					gap            : 1,
					mb             : 2,
					maxHeight      : '40vh',
					overflowY      : 'auto',
					width          : '100%',
					minHeight      : '60px',
					alignItems     : 'center',
					justifyContent : searchQuery && filteredTags.length === 0 ? 'center' : 'flex-start'
				}}
				>
					{filteredTags.length > 0
						? (
							renderTagChips
						)
						: searchQuery
							? (
								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ fontStyle: 'italic' }}
								>
									{language?.noTagsFound || 'No tags found'}
								</Typography>
							)
							: null}
				</Box>
			)}

			{/* Input field for new tags */}
			<Box width={'100%'} pb={2}>
				<TextField
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={language?.enterNewTag || 'Enter new tag'}
					fullWidth
					variant='outlined'
					size='small'
				/>
			</Box>

			{/* Add Tag button */}
			<Button
				onClick={handleAddTag}
				variant='contained'
				fullWidth
				disabled={!inputValue.trim()}
				sx={ADD_BUTTON_STYLES}
			>
				{language?.addTags || 'Add Tags'}
			</Button>
		</Grid>
	);
};

export default React.memo(TagSlider);
